import { ReactNode, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, Flex, Input } from 'antd'
import type { DataIndex } from 'rc-table/lib/interface'
import { SearchOutlined } from '@ant-design/icons'
import type { FilterDropdownProps } from 'antd/es/table/interface'
import Highlighter from 'react-highlight-words'

/**
 * Хук для поиска и подсветки значений в колонке Ant Design Table.
 * Значение input можно получить в обработчике onChange таблицы в параметре filters
 */

type Params<T> = {
  placeholder: string
  dataIndex?: DataIndex
  customRender?: (highlightedValue: ReactNode, record: T) => ReactNode
}

export const useColumnSearch = <T extends Record<string, unknown>>({
  dataIndex,
  placeholder,
  customRender,
}: Params<T>) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const param = Array.isArray(dataIndex) ? dataIndex.join('.') : dataIndex
  const initialValue = searchParams.get(`searchField_${param}`)

  const [searchText, setSearchText] = useState<string | undefined>(
    initialValue ?? '',
  )
  const [isEmpty, setIsEmpty] = useState(false)

  const filterDropdown = ({
    setSelectedKeys,
    confirm,
    clearFilters,
  }: FilterDropdownProps) => {
    return (
      <Flex vertical gap={8} style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${String(placeholder)}`}
          value={searchText}
          status={isEmpty ? 'error' : undefined}
          onChange={({ target }) => {
            setIsEmpty(false)
            const text = target.value

            setSearchText(text)
            if (!text) clearFilters?.()
          }}
        />
        <Flex gap={8} justify="space-between">
          <Button
            icon={<SearchOutlined />}
            size="small"
            style={{ flex: 1 }}
            type="primary"
            onClick={() => {
              if (!searchText) {
                setIsEmpty(true)
                return
              }
              setSelectedKeys([searchText])
              confirm({ closeDropdown: false })
            }}
          >
            Search
          </Button>
          <Button
            size="small"
            style={{ flex: 1 }}
            onClick={() => {
              clearFilters?.()
              setSearchText('')
              const params = new URLSearchParams(searchParams)
              params.delete(`searchField_${param}`)
              setSearchParams(params)
              confirm({ closeDropdown: false })
            }}
          >
            Reset
          </Button>
        </Flex>
      </Flex>
    )
  }

  const filterIcon = () => (
    <SearchOutlined
      style={{ color: searchText ? 'var(--color-primary)' : undefined }}
    />
  )

  const render = (value: unknown, record: T) => {
    const highlightedValue = (
      <Highlighter
        searchWords={[searchText ?? '']}
        highlightStyle={{
          backgroundColor: 'var(--color-yellow-50)',
          padding: 0,
        }}
        autoEscape
        textToHighlight={String(value ?? '')}
      />
    )

    if (customRender) {
      return customRender(highlightedValue, record)
    }

    return highlightedValue
  }

  return { filterDropdown, filterIcon, render }
}
